import delay from "src/utils/delay";
import DeDupQueue from "./DeDupQueue";
import ServerDownError from "./errors/ServerDown";
import NetworkInfo from "./NetworkInfo";
export class SyncBoxManager {
    registeredSyncBoxes = new Map();
    syncTimeouts = new Map();
    syncQueue = new DeDupQueue();
    isProcessingQueue = false;
    stopProcessingQueue = false;
    networkInfo;
    isInitialized = false;
    constructor() {
        this.networkInfo = new NetworkInfo();
    }
    /**
     * Initialize the sync manager and start auto-sync if online
     */
    init() {
        if (this.isInitialized) {
            return;
        }
        this.networkInfo.init();
        this.startQueueProcessing();
        // Setup timeouts for all previously registered syncBoxes
        this.setupAllRegisteredSyncBoxes();
        this.isInitialized = true;
    }
    /**
     * Register a syncbox instance with the manager
     * @param syncBox - The syncbox instance to register
     * @param options - Optional events object
     * @returns The registered syncbox instance
     */
    async register(syncBox, options) {
        // Check if syncBox is already registered
        if (this.registeredSyncBoxes.has(syncBox)) {
            // Update events if provided
            if (options?.events) {
                this.registeredSyncBoxes.set(syncBox, { events: options.events });
            }
            return syncBox;
        }
        await syncBox.init();
        this.registeredSyncBoxes.set(syncBox, { events: options?.events });
        // Only setup timeout if manager is already initialized
        if (this.isInitialized) {
            this.setupSyncBoxTimeout(syncBox);
        }
    }
    /**
     * Deregister a syncbox instance from the manager
     * @param syncBox - The syncbox instance to deregister
     * @returns true if the syncbox was deregistered, false if it wasn't found
     */
    deregister(syncBox) {
        if (!this.registeredSyncBoxes.has(syncBox)) {
            return false;
        }
        // Clear the timeout for this syncBox
        this.clearSyncBoxTimeout(syncBox);
        this.registeredSyncBoxes.delete(syncBox);
        return true;
    }
    /**
     * Setup timeouts for all registered syncBoxes
     */
    setupAllRegisteredSyncBoxes() {
        for (const syncBox of this.registeredSyncBoxes.keys()) {
            this.setupSyncBoxTimeout(syncBox);
        }
    }
    /**
     * Setup timeout for a specific syncBox
     * @param syncBox - The syncBox to setup timeout for
     */
    async setupSyncBoxTimeout(syncBox) {
        // Clear any existing timeout for this syncBox
        this.clearSyncBoxTimeout(syncBox);
        const syncInterval = (await syncBox.getConfig()).syncInterval;
        const lastSyncedInfo = await (await syncBox.getState()).getLastSyncedInfo();
        const lastSyncedAt = lastSyncedInfo.lastSyncedAt ?? 0;
        // Calculate initial timeout based on time difference between lastSyncedAt and syncInterval
        const now = Date.now();
        const timeSinceLastSync = now - lastSyncedAt;
        const initialTimeout = Math.max(0, syncInterval - timeSinceLastSync);
        const timeout = setTimeout(async () => {
            // Add syncBox to queue and wait for it to be processed
            await this.addToQueueAndWait(syncBox).catch(error => {
                console.error('Error adding syncBox to queue:', error);
            });
            // Setup subsequent timeout with full syncInterval duration
            this.setupSyncBoxTimeout(syncBox);
        }, initialTimeout);
        this.syncTimeouts.set(syncBox, timeout);
    }
    /**
     * Clear timeout for a specific syncBox
     * @param syncBox - The syncBox to clear timeout for
     */
    clearSyncBoxTimeout(syncBox) {
        const timeout = this.syncTimeouts.get(syncBox);
        if (timeout) {
            clearTimeout(timeout);
            this.syncTimeouts.delete(syncBox);
        }
    }
    /**
     * Add syncBox to queue and wait for it to be processed
     * @param syncBox - The syncBox to add to queue
     */
    async addToQueueAndWait(syncBox) {
        return new Promise(async (resolve, reject) => {
            // Add to queue (will be ignored if already present)
            this.syncQueue.push((await syncBox.getConfig()).syncBoxId, { syncBox, resolve, reject });
        });
    }
    /**
     * Start processing the sync queue
     */
    startQueueProcessing() {
        this.stopProcessingQueue = false;
        this.processQueue();
    }
    /**
     * Process items in the sync queue
     */
    async processQueue() {
        if (this.isProcessingQueue) {
            return;
        }
        this.isProcessingQueue = true;
        while (!this.stopProcessingQueue) {
            await this.syncQueue.waitForNextItem();
            await this.networkInfo.waitForOnline();
            const queueItem = this.syncQueue.pop();
            if (!queueItem) {
                continue;
            }
            const { syncBox, resolve, reject } = queueItem;
            try {
                await this.syncSingleSyncBox(syncBox);
                resolve();
            }
            catch (error) {
                if (error instanceof ServerDownError) {
                    console.error('Server down error in queue processing:', error.message);
                    // For server down errors, we might want to retry later
                    // For now, reject the promise but log the specific error type
                    reject(error);
                }
                else {
                    console.error('Error syncing changes for syncbox:', error);
                    reject(error);
                }
            }
        }
    }
    stopQueueProcessing() {
        this.stopProcessingQueue = true;
    }
    /**
     * Sync a single syncBox
     * @param syncBox - The syncBox to sync
     */
    async syncSingleSyncBox(syncBox) {
        if (!this.networkInfo.getOnlineStatus()) {
            return;
        }
        try {
            // First push changes for this syncbox
            await syncBox.pushChanges();
            // Then pull changes for this syncbox
            await syncBox.pullChanges();
            // Then update the syncbox state
            await (await syncBox.getState()).updateLastSyncedInfo({
                lastSyncedAt: Date.now(),
            });
            // Call onSynced event if registered
            const entry = this.registeredSyncBoxes.get(syncBox);
            if (entry?.events?.onSynced) {
                try {
                    entry.events.onSynced();
                }
                catch (err) {
                    console.error('Error in onSynced callback:', err);
                }
            }
        }
        catch (error) {
            if (error instanceof ServerDownError) {
                console.error('Server down error syncing changes for syncbox:', error.message);
                // Don't retry immediately if server is down, let the network info handle reconnection
                if (error.retryAfter) {
                    await delay(error.retryAfter);
                }
            }
            else {
                console.error('Error syncing changes for syncbox:', error);
            }
        }
    }
    /**
     * Stop automatic syncing for all syncBoxes
     */
    stopAutoSync() {
        // Clear all timeouts
        for (const syncBox of this.syncTimeouts.keys()) {
            this.clearSyncBoxTimeout(syncBox);
        }
        this.syncTimeouts.clear();
    }
    /**
     * Cleanup method to remove event listeners
     * Should be called when the manager is no longer needed
     */
    destroy() {
        this.stopAutoSync();
        this.stopQueueProcessing();
        this.syncQueue.clear();
        this.networkInfo.destroy();
        this.isInitialized = false;
    }
}
const syncBoxManager = new SyncBoxManager();
export default syncBoxManager;
